// import React, { createContext, useContext, useState, useEffect } from "react";
// import { APICore } from "../helpers/api/apiCore";

// interface LocalStorageContextProps {
//   contentTypeID: any;
//   creatorDetails: any;
//   channelDetails: any;
//   contentTypeCategoryDetails: any;
//   monetisationTypeID: number;
//   revenuePlanID: number;
//   loading: boolean; // ✅ Add loading state
// }

// const api = new APICore();

// const LocalStorageContext = createContext<LocalStorageContextProps | undefined>(
//   undefined
// );

// export const LocalStorageProvider: React.FC<{ children: React.ReactNode }> = ({
//   children,
// }) => {
//   const [loading, setLoading] = useState(true); // ✅ Manage loading state

//   const [contentTypeID, setContentTypeID] = useState<any>(null);
//   const [creatorDetails, setCreatorDetails] = useState<any>(null);
//   const [channelDetails, setChannelDetails] = useState<any>(null);
//   const [contentTypeCategoryDetails, setContentTypeCategoryDetails] =
//     useState<any>(null);
//   const [monetisationTypeID, setMonetisationTypeID] = useState<number>(0);
//   const [revenuePlanID, setRevenuePlanID] = useState<number>(0);

//   // Utility function to safely parse JSON from localStorage
//   const getFromLocalStorage = (key: string) => {
//     try {
//       return JSON.parse(localStorage.getItem(key) || "null");
//     } catch (e) {
//       console.warn(`Error parsing ${key} from localStorage`, e);
//       return null;
//     }
//   };

//   // Function to update values from localStorage
//   const updateStorageValues = () => {
//     setLoading(true); // ✅ Start loading

//     const newContentTypeID = getFromLocalStorage("contentTypeID");
//     const newCreatorDetails = getFromLocalStorage("creatorDetails");
//     const newChannelDetails = getFromLocalStorage("channelDetails");
//     const newContentTypeCategoryDetails = getFromLocalStorage(
//       "contentTypeCategoryDetails"
//     );
//     const newMonetisationTypeID =
//       Number(getFromLocalStorage("monetisationTypeID")) || 0;
//     const newRevenuePlanID = Number(getFromLocalStorage("reveneuePlanID")) || 0;

//     setContentTypeID(newContentTypeID);
//     setCreatorDetails(newCreatorDetails);
//     setChannelDetails(newChannelDetails);
//     setContentTypeCategoryDetails(newContentTypeCategoryDetails);
//     setMonetisationTypeID(newMonetisationTypeID);
//     setRevenuePlanID(newRevenuePlanID);

//     setLoading(false); // ✅ Data fetch complete
//   };

//   useEffect(() => {
//     let isMounted = true; // ✅ Add a flag for component mount status

//     const handleStorageChange = (event: StorageEvent) => {
//       if (
//         isMounted &&
//         [
//           "contentTypeID",
//           "creatorDetails",
//           "channelDetails",
//           "contentTypeCategoryDetails",
//           "monetisationTypeID",
//           "reveneuePlanID",
//         ].includes(event.key!)
//       ) {
//         updateStorageValues();
//       }
//     };

//     window.addEventListener("storage", handleStorageChange);

//     return () => {
//       isMounted = false; // ✅ Clean up on unmount
//       window.removeEventListener("storage", handleStorageChange);
//     };
//   }, []);

//   useEffect(() => {
//     let isMounted = true; // ✅ Add a flag for component mount status
//     setLoading(true);

//     if (isMounted) {
//       updateStorageValues(); // ✅ Initial fetch
//     }

//     const interval = setInterval(() => {
//       if (isMounted) {
//         updateStorageValues();
//       }
//     }, 500); // ✅ Increased interval to avoid rapid re-renders

//     return () => {
//       isMounted = false; // ✅ Clean up on unmount
//       clearInterval(interval);
//     };
//   }, []);

//   return (
//     <>
//       <LocalStorageContext.Provider
//         value={{
//           contentTypeID,
//           creatorDetails,
//           channelDetails,
//           contentTypeCategoryDetails,
//           monetisationTypeID,
//           revenuePlanID,
//           loading, // ✅ Provide loading state
//         }}
//       >
//         {children}
//       </LocalStorageContext.Provider>
//     </>
//   );
// };

// export const useLocalStorageData = (): LocalStorageContextProps => {
//   const context = useContext(LocalStorageContext);
//   if (!context) {
//     throw new Error(
//       "useLocalStorageData must be used within a LocalStorageProvider"
//     );
//   }
//   return context;
// };



import React, { createContext, useContext, useEffect, useState } from "react";

interface LocalStorageContextProps {
  dateFilter: any;
  showHelpMenuValue: any;
  contentTypeID: any;
  creatorDetails: any;
  channelDetails: any;
  contentTypeCategoryDetails: any;
  isFromOutside: number;
  monetisationTypeID: number;
  revenuePlanID: number;
  loading: boolean;
}

const LocalStorageContext = createContext<LocalStorageContextProps | undefined>(undefined);

// 🆕 Utility to safely parse localStorage
const getFromLocalStorage = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch (e) {
    console.warn(`Error parsing ${key} from localStorage`, e);
    return null;
  }
};

const isOutside = Number(getFromLocalStorage("isFromOutside")) || 0;

// 🆕 Central function to fetch all required data at once
const fetchStorageValues = () => ({
  dateFilter: getFromLocalStorage("dateFilter"),
  showHelpMenuValue: getFromLocalStorage("showHelpMenu"),
  contentTypeID: getFromLocalStorage("contentTypeID"),
  creatorDetails: getFromLocalStorage("creatorDetails"),
  channelDetails: getFromLocalStorage("channelDetails"),
  contentTypeCategoryDetails: getFromLocalStorage("contentTypeCategoryDetails"),
  isFromOutside: Number(getFromLocalStorage("isFromOutside")) || 0,
  monetisationTypeID: Number(getFromLocalStorage("monetisationTypeID")) || 0,
  revenuePlanID: isOutside !== 1 ? Number(getFromLocalStorage("reveneuePlanID")) || 0 : -200,
});

export const LocalStorageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [storageData, setStorageData] = useState<LocalStorageContextProps>({
    ...fetchStorageValues(),
    loading: true,
  });

  // 🆕 Function to update state from localStorage
  const updateStorageValues = () => {
    setStorageData({ ...fetchStorageValues(), loading: false });
  };

  useEffect(() => {
    // Initial load
    updateStorageValues();

    // Listen to localStorage changes across all tabs/windows
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key && Object.keys(storageData).includes(event.key)) {
        updateStorageValues();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Optional: Set an interval to handle non-storage event changes
    const interval = setInterval(updateStorageValues, 1000); // Increased interval for stability

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return (
    <LocalStorageContext.Provider value={storageData}>
      {children}
    </LocalStorageContext.Provider>
  );
};

// 🆕 Custom Hook to use the context
export const useLocalStorageData = (): LocalStorageContextProps => {
  const context = useContext(LocalStorageContext);
  if (!context) {
    throw new Error("useLocalStorageData must be used within a LocalStorageProvider");
  }
  return context;
};


